import {
  ActivatedRouteSnapshot,
  DetachedRouteHandle,
  RouteReuseStrategy,
} from "@angular/router";

/**
 * Keeps the CAIRA tab components (home + tracker) alive when switching between
 * them, instead of Angular's default of destroying and recreating the routed
 * component on every navigation.
 *
 * Only routes that declare `data: { reuseKey: '...' }` are cached; every other
 * route (login, masterclass, chapter, exam, etc.) keeps the default behaviour.
 * This removes the flicker that happened when switching CAIRA <-> CAIRA Tracker,
 * which was caused by the components being recreated and re-loading their data
 * (visible only on the first switch after a reload, before the browser cached
 * the responses).
 */
export class CairaRouteReuseStrategy implements RouteReuseStrategy {
  private handles = new Map<string, DetachedRouteHandle>();

  private keyOf(route: ActivatedRouteSnapshot): string | null {
    return (route.data && (route.data["reuseKey"] as string)) || null;
  }

  /** Detach (cache) only routes flagged with a reuseKey. */
  shouldDetach(route: ActivatedRouteSnapshot): boolean {
    return !!this.keyOf(route);
  }

  store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle | null): void {
    const key = this.keyOf(route);
    if (!key) return;
    if (handle) {
      this.handles.set(key, handle);
    } else {
      this.handles.delete(key);
    }
  }

  /** Re-attach a cached component if we have one for this route. */
  shouldAttach(route: ActivatedRouteSnapshot): boolean {
    const key = this.keyOf(route);
    return !!key && this.handles.has(key);
  }

  retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
    const key = this.keyOf(route);
    return key ? this.handles.get(key) ?? null : null;
  }

  /** Default: reuse when navigating to the same route configuration. */
  shouldReuseRoute(
    future: ActivatedRouteSnapshot,
    curr: ActivatedRouteSnapshot
  ): boolean {
    return future.routeConfig === curr.routeConfig;
  }
}
